import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {apiService} from '../services/api';
import {useAdmin} from './AdminContext';
import {formatDate, formatPrice} from './adminUtils';

interface AdminOrderSummary {
  id: string;
  userId: string;
  userEmail?: string | null;
  paymentMethod?: string | null;
  status: string;
  paymentStatus: string;
  totalAmount: number;
  createdAt: string;
  itemCount: number;
  test: boolean;
  productId?: string | null;
  productName?: string | null;
}

interface AdminOrderDetail {
  order: any;
  totalAmount: number;
  userEmail?: string | null;
  product?: any;
  paymentCheckStatus?: string | null;
}

const AdminOrdersPage: React.FC = () => {
  const {token} = useAdmin();
  const [orders, setOrders] = useState<AdminOrderSummary[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<AdminOrderDetail | null>(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [userIdFilter, setUserIdFilter] = useState('');
  const [checkingOrder, setCheckingOrder] = useState(false);
  const [markingTest, setMarkingTest] = useState(false);

  const loadOrders = useCallback(async () => {
    if (!token) return;
    setLoadingOrders(true);
    setDataError(null);
    try {
      const data = await apiService.adminGetOrders(token, {
        status: statusFilter || undefined,
        paymentStatus: paymentFilter || undefined,
        includeTest,
        userId: userIdFilter.trim() || undefined,
      });
      setOrders(data);
      if (data.length) {
        setSelectedOrderId((prev) => prev ?? data[0].id);
      } else {
        setSelectedOrderId(null);
        setSelectedOrder(null);
      }
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesno ucitavanje ordera.');
    } finally {
      setLoadingOrders(false);
    }
  }, [token, statusFilter, paymentFilter, includeTest, userIdFilter]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const loadOrderDetails = useCallback(async (orderId: string) => {
    if (!token) return;
    setLoadingDetails(true);
    setDataError(null);
    try {
      const details = await apiService.adminGetOrderDetails(token, orderId);
      setSelectedOrder(details);
    } catch (error) {
      setSelectedOrder(null);
      setDataError(error instanceof Error ? error.message : 'Neuspesno ucitavanje detalja.');
    } finally {
      setLoadingDetails(false);
    }
  }, [token]);

  useEffect(() => {
    if (!selectedOrderId) return;
    loadOrderDetails(selectedOrderId);
  }, [loadOrderDetails, selectedOrderId]);

  const handleCheckTransaction = async () => {
    if (!token || !selectedOrderId) return;
    setCheckingOrder(true);
    setDataError(null);
    try {
      const result = await apiService.adminCheckOrderTransaction(token, selectedOrderId);
      if (result && result.ok === false) {
        const responseCode = String(result.responseCode || '').toUpperCase();
        if (responseCode === 'SCHEDULER_ACTIVE') {
          setDataError('Scheduler je aktivan. Provera je zakljucana.');
          return;
        }
        if (responseCode === 'NO_TRANSACTION') {
          setDataError('Nema transakcije za ovaj order.');
          return;
        }
        if (responseCode === 'ALREADY_SUCCESS') {
          setDataError('Order je vec uspesan.');
          return;
        }
        setDataError('Neuspesna provera transakcije.');
        return;
      }
      await loadOrders();
      await loadOrderDetails(selectedOrderId);
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesna provera transakcije.');
    } finally {
      setCheckingOrder(false);
    }
  };

  const handleMarkTest = async () => {
    if (!token || !selectedOrderId) return;
    if (markingTest) return;
    setMarkingTest(true);
    setDataError(null);
    try {
      await apiService.adminMarkOrderTest(token, selectedOrderId);
      await loadOrders();
      await loadOrderDetails(selectedOrderId);
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesno oznacavanje ordera.');
    } finally {
      setMarkingTest(false);
    }
  };

  const selectedSummary = useMemo(
    () => orders.find((order) => order.id === selectedOrderId) || null,
    [orders, selectedOrderId]
  );
  const isOrderSuccess = useMemo(() => {
    const status = (selectedOrder?.order?.status || selectedSummary?.status || '').toUpperCase();
    const paymentStatus = (selectedOrder?.order?.paymentStatus || selectedSummary?.paymentStatus || '').toUpperCase();
    return status === 'PAYMENT_SUCCESS' || status === 'SUCCESS' || paymentStatus === 'SUCCESS';
  }, [selectedOrder, selectedSummary]);
  const isSchedulerActive = useMemo(() => {
    const status = (selectedOrder?.paymentCheckStatus || '').toUpperCase();
    if (status === 'PENDING' || status === 'PROCESSING') return true;
    const orderStatus = (selectedOrder?.order?.status || '').toUpperCase();
    const paymentStatus = (selectedOrder?.order?.paymentStatus || '').toUpperCase();
    return !status && (orderStatus === 'PAYMENT_PENDING' || paymentStatus === 'PENDING');
  }, [selectedOrder]);
  const paymentCheckStatus = selectedOrder?.paymentCheckStatus;
  const schedulerMessage = useMemo(() => {
    if (!paymentCheckStatus) return null;
    const status = paymentCheckStatus.toUpperCase();
    if (status === 'PENDING' || status === 'PROCESSING') {
      return `Automatska provera u toku (${paymentCheckStatus})`;
    }
    return `Scheduler: ${paymentCheckStatus}`;
  }, [paymentCheckStatus]);
  const canActOnOrder = Boolean(selectedOrderId && selectedOrder && !loadingDetails);

  const handleRefresh = async () => {
    await loadOrders();
    if (selectedOrderId) {
      await loadOrderDetails(selectedOrderId);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold text-neutral-900">Orderi</h2>
        <p className="mt-1 text-neutral-500">Pracenje placanja i statusa.</p>
      </div>

      {dataError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {dataError}
        </div>
      )}

      <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
        <div className="flex flex-wrap items-center gap-3">
          <input
            className="input-field max-w-xs"
            placeholder="User ID"
            value={userIdFilter}
            onChange={(event) => setUserIdFilter(event.target.value)}
          />
          <select
            className="input-field max-w-[200px]"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">Svi statusi</option>
            <option value="CREATED">CREATED</option>
            <option value="PAYMENT_PENDING">PAYMENT_PENDING</option>
            <option value="PAYMENT_SUCCESS">PAYMENT_SUCCESS</option>
            <option value="PAYMENT_FAILED">PAYMENT_FAILED</option>
            <option value="CANCELLED">CANCELLED</option>
          </select>
          <select
            className="input-field max-w-[200px]"
            value={paymentFilter}
            onChange={(event) => setPaymentFilter(event.target.value)}
          >
            <option value="">Sva placanja</option>
            <option value="PENDING">PENDING</option>
            <option value="SUCCESS">SUCCESS</option>
            <option value="FAILED">FAILED</option>
            <option value="CANCELLED">CANCELLED</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-neutral-600">
            <input
              type="checkbox"
              checked={includeTest}
              onChange={(event) => setIncludeTest(event.target.checked)}
            />
            Ukljuci test order
          </label>
          <button className="btn-outline" type="button" onClick={loadOrders} disabled={loadingOrders}>
            {loadingOrders ? 'Ucitavam...' : 'Primeni filtere'}
          </button>
          <button className="btn-outline" type="button" onClick={handleRefresh} disabled={loadingOrders || loadingDetails}>
            {loadingOrders || loadingDetails ? 'Osvezavam...' : 'Osvezi'}
          </button>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[420px_1fr]">
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-neutral-900">Lista ordera</h3>
            <span className="text-sm text-neutral-500">{orders.length}</span>
          </div>
          {loadingOrders ? (
            <div className="space-y-3">
              <div className="h-16 rounded-xl bg-neutral-100" />
              <div className="h-16 rounded-xl bg-neutral-100" />
              <div className="h-16 rounded-xl bg-neutral-100" />
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => setSelectedOrderId(order.id)}
                  className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                    order.id === selectedOrderId
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-neutral-900">Order {order.id.slice(0, 8)}</p>
                      <p className="text-sm text-neutral-500">{order.userEmail || order.userId}</p>
                    </div>
                    <span className="text-xs font-semibold text-neutral-500">{order.paymentStatus}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-sm text-neutral-500">
                    <span>{formatPrice(order.totalAmount, null)}</span>
                    <span>{formatDate(order.createdAt)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-neutral-900">
                  {selectedSummary ? `Order ${selectedSummary.id.slice(0, 8)}` : 'Order'}
                </h3>
                <p className="text-sm text-neutral-500">{selectedSummary?.userEmail || '-'}</p>
              </div>
              <div className="text-sm text-neutral-500">
                <span>Status: {selectedSummary?.status || '-'}</span>
              </div>
            </div>

            {loadingDetails ? (
              <div className="h-32 rounded-xl bg-neutral-100" />
            ) : !selectedOrder ? (
              <p className="text-neutral-500">Izaberite order da vidite detalje.</p>
            ) : (
              <div className="space-y-4">
                {schedulerMessage && (
                  <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-600">
                    {schedulerMessage}
                  </div>
                )}
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5 text-sm text-neutral-600">
                  <div className="grid gap-2 md:grid-cols-2">
                    <span>Placanje: {selectedOrder.order?.paymentStatus}</span>
                    <span>Metod: {selectedOrder.order?.paymentMethod || '-'}</span>
                    <span>Order status: {selectedOrder.order?.status}</span>
                    <span>Ukupno: {formatPrice(selectedOrder.totalAmount, null)}</span>
                    {selectedOrder.order?.paymentId ? (
                      <span>Payment ID: {selectedOrder.order?.paymentId}</span>
                    ) : null}
                    {selectedOrder.order?.paymentRrn ? (
                      <span>RRN: {selectedOrder.order?.paymentRrn}</span>
                    ) : null}
                    {selectedOrder.order?.paymentTxId ? (
                      <span>TX ID: {selectedOrder.order?.paymentTxId}</span>
                    ) : null}
                    <span>Test: {selectedOrder.order?.test ? 'DA' : 'NE'}</span>
                  </div>
                </div>
                {selectedOrder.product && (
                  <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                    <p className="text-sm text-neutral-500">Product</p>
                    <p className="mt-2 text-sm text-neutral-900">{selectedOrder.product?.name}</p>
                    <p className="text-xs text-neutral-500">Tokeni: {selectedOrder.product?.tokens}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
            <h4 className="text-xl font-semibold text-neutral-900">Akcije</h4>
            <div className="mt-5 rounded-xl border border-neutral-200 bg-neutral-50 p-5">
              <p className="text-sm text-neutral-500">Placanje</p>
              <button
                className="btn-outline mt-3 w-full"
                type="button"
                onClick={handleCheckTransaction}
                disabled={!canActOnOrder || checkingOrder || isOrderSuccess || isSchedulerActive}
              >
                {checkingOrder ? 'Proveravam...' : 'Proveri transakciju'}
              </button>
              <button
                className="btn-outline mt-3 w-full"
                type="button"
                onClick={handleMarkTest}
                disabled={!canActOnOrder || markingTest}
              >
                {markingTest ? 'Oznacavam...' : 'Oznaci kao test'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrdersPage;
